import { useState } from 'react';
import { CalendarDaysIcon, MagnifyingGlassIcon, MapPinIcon, ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useJobCards, useSyncJobCards, useJobClaims, useSetClaimStatus } from '../hooks/useJobCards';

function fmtTime(v) {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' });
}
function Chip({ label, value, tone = 'gray', onClick, active }) {
  const tones = { gray: 'bg-gray-100 text-gray-700', sky: 'bg-sky-100 text-sky-800', indigo: 'bg-indigo-100 text-indigo-800', amber: 'bg-amber-100 text-amber-800', emerald: 'bg-emerald-100 text-emerald-800', red: 'bg-red-100 text-red-800' };
  const body = <><div className="text-lg font-bold tabular-nums">{value ?? 0}</div><div className="text-xs font-medium">{label}</div></>;
  if (!onClick) return <div className={`rounded-lg px-3 py-2 ${tones[tone]}`}>{body}</div>;
  // Clickable so the number is a way in, not just a figure to read: tapping it
  // filters the table to exactly those jobs.
  return (
    <button type="button" onClick={onClick}
      className={`rounded-lg px-3 py-2 text-left ${tones[tone]} ${active ? 'ring-2 ring-offset-1 ring-red-500' : 'hover:brightness-95'}`}>
      {body}
    </button>
  );
}
function SlotBadge({ slot }) {
  const s = (slot || '').toUpperCase();
  const cls = s === 'AM' ? 'bg-sky-100 text-sky-800' : s === 'PM' ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-600';
  return <span className={`rounded px-1.5 py-0.5 text-xs font-bold ${cls}`}>{s || '—'}</span>;
}
function ExtBadge({ e }) {
  if (!e) return null;
  const og = e.toUpperCase() === 'OVERHEAD';
  const ug = e.toUpperCase() === 'UNDERGROUND';
  if (!og && !ug) return <span className="text-xs text-gray-400">{e}</span>;
  return <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${og ? 'bg-amber-100 text-amber-800' : 'bg-teal-100 text-teal-800'}`}>{og ? 'OH' : 'UG'}</span>;
}

function fmtDay(v) {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB');
}

function fmtWhen(v) {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString('en-GB')} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
}

// Engineers who cannot see their job claim it by WO number from the app. Every
// row here is a job card whose assignment did not reach the person doing the
// work, so this is both a to-do list and the health signal for the SharePoint
// feed: a handful is a normal morning, thirty before lunch means it broke.
//
// A queue rather than an email alert, because an inbox carries no state -- two
// coordinators fix the same claim while a third sits unread behind someone's
// leave, and nothing shows you the volume.
function ClaimsQueue() {
  const [status, setStatus] = useState('open');
  const { data, isLoading } = useJobClaims(status);
  const setClaim = useSetClaimStatus();
  const claims = data?.claims || [];

  const TABS = [['open', 'Waiting'], ['resolved', 'Resolved'], ['rejected', 'Rejected']];
  const open = status === 'open';

  return (
    <div className={`mt-4 rounded-lg border ${open && claims.length ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white'}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
        <h2 className="flex items-center gap-2 text-sm font-bold text-gray-900">
          <ExclamationTriangleIcon className={`h-5 w-5 ${open && claims.length ? 'text-amber-600' : 'text-gray-300'}`} />
          Jobs engineers could not see
          {open && claims.length > 0 && (
            <span className="rounded-full bg-amber-200 px-2 py-0.5 text-xs font-bold text-amber-900">{claims.length}</span>
          )}
        </h2>
        <div className="flex gap-1">
          {TABS.map(([k, lbl]) => (
            <button key={k} type="button" onClick={() => setStatus(k)}
              className={`rounded-full px-3 py-1 text-xs font-semibold ${status === k ? 'bg-brand text-white' : 'bg-white text-gray-600 border border-gray-300'}`}>
              {lbl}
            </button>
          ))}
        </div>
      </div>

      {isLoading && <p className="px-4 pb-3 text-sm text-gray-400">Loading…</p>}

      {!isLoading && claims.length === 0 && (
        <p className="px-4 pb-3 text-sm text-gray-500">
          {open ? 'Nothing waiting — every engineer can see their jobs.' : 'Nothing here.'}
        </p>
      )}

      {!isLoading && claims.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-y border-gray-200 bg-white/60 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-2 font-semibold">WO</th>
                <th className="px-4 py-2 font-semibold">Engineer</th>
                <th className="px-4 py-2 font-semibold">Job</th>
                <th className="px-4 py-2 font-semibold">Assigned to</th>
                <th className="px-4 py-2 font-semibold">Reported</th>
                {open && <th className="px-4 py-2 font-semibold" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {claims.map((c) => (
                <tr key={c.id} className="align-top">
                  <td className="whitespace-nowrap px-4 py-2 font-mono font-semibold text-gray-900">{c.wo_number}</td>
                  <td className="px-4 py-2 text-gray-800">{c.engineer || '(unknown)'}</td>
                  <td className="px-4 py-2 text-gray-600">
                    {Number(c.matched_jobs) === 0 ? (
                      // Not a mistake on the engineer's part: the card may simply
                      // not have synced yet. It links itself when it arrives.
                      <span className="text-amber-700">Not on the board yet</span>
                    ) : (
                      <>
                        <span className="block text-gray-800">{c.address}</span>
                        <span className="text-xs text-gray-500">
                          {fmtDay(c.installation_date)}{c.region ? ` · ${c.region}` : ''}
                          {Number(c.matched_jobs) > 1 ? ` · ${c.matched_jobs} cards share this WO` : ''}
                        </span>
                      </>
                    )}
                  </td>
                  <td className="px-4 py-2 text-gray-600">{c.assigned_to || '—'}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-xs text-gray-500">{fmtWhen(c.created_at)}</td>
                  {open && (
                    <td className="whitespace-nowrap px-4 py-2 text-right">
                      <button type="button" disabled={setClaim.isPending}
                        onClick={() => setClaim.mutate({ id: c.id, action: 'resolve' })}
                        className="rounded-md bg-brand px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-50">
                        Put right
                      </button>
                      <button type="button" disabled={setClaim.isPending}
                        onClick={() => setClaim.mutate({ id: c.id, action: 'reject' })}
                        className="ml-1 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-600 disabled:opacity-50">
                        Not theirs
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function JobCards() {
  const [date, setDate] = useState('');   // '' = server default (today)
  const [region, setRegion] = useState('');
  const [slot, setSlot] = useState('');
  const [externals, setExternals] = useState('');
  const [q, setQ] = useState('');

  const [syncMsg, setSyncMsg] = useState(null);
  const sync = useSyncJobCards();
  const doSync = () => {
    setSyncMsg(null);
    sync.mutate(undefined, {
      onSuccess: (r) => setSyncMsg(r?.skipped ? 'A sync is already running…' : `Synced — ${r?.jobs_upserted ?? 0} job cards refreshed`),
      onError: () => setSyncMsg('Sync failed — try again'),
    });
  };

  const [flag, setFlag] = useState('');
  const { data, isLoading, isError } = useJobCards({ date, region, slot, externals, q, flag, limit: 1000 });
  const jobs = data?.jobs || [];
  const s = data?.summary || {};

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900"><CalendarDaysIcon className="h-6 w-6 text-brand" /> Job Cards</h1>
          <p className="mt-1 text-sm text-gray-500">Scheduler board — all install job cards, synced live from SharePoint every 10 minutes. Read-only.</p>
        </div>
        <div className="flex items-center gap-2">
          {syncMsg && <span className="text-xs text-gray-500">{syncMsg}</span>}
          <button type="button" onClick={doSync} disabled={sync.isPending}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            <ArrowPathIcon className={`h-4 w-4 ${sync.isPending ? 'animate-spin' : ''}`} /> {sync.isPending ? 'Syncing…' : 'Sync now'}
          </button>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-md border border-gray-300 px-3 py-2 text-sm" />
        </div>
      </div>

      <ClaimsQueue />

      <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-6">
        <Chip label="Total" value={s.total} />
        <Chip label="Region N" value={s.region_n} sky />
        <Chip label="Region B" value={s.region_b} indigo />
        <Chip label="AM" value={s.am} />
        <Chip label="Stock logged" value={s.stock_done} tone="emerald" />
        {/* Finished in the field, nothing logged. The materials left a van and
            were recorded nowhere, so the van still shows them and the reorder
            never fires. */}
        <Chip label="Done, no stock" value={s.complete_no_stock} tone="red"
          active={flag === 'no_stock'}
          onClick={() => setFlag(flag === 'no_stock' ? '' : 'no_stock')} />
      </div>
      {flag === 'no_stock' && (
        <p className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          Showing jobs marked complete in the field with <strong>no stock logged against them</strong>.
          Usually the engineer was blocked by the weekly count, or submitted an empty card.
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {[['', 'All regions'], ['N', 'Region N'], ['B', 'Region B']].map(([v, l]) => (
          <button key={v} type="button" onClick={() => setRegion(v)} className={`rounded-full px-3 py-1.5 text-sm font-medium ${region === v ? 'bg-brand text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>{l}</button>
        ))}
        <select value={slot} onChange={(e) => setSlot(e.target.value)} className="rounded-md border border-gray-300 px-2 py-1.5 text-sm"><option value="">AM &amp; PM</option><option value="AM">AM</option><option value="PM">PM</option></select>
        <select value={externals} onChange={(e) => setExternals(e.target.value)} className="rounded-md border border-gray-300 px-2 py-1.5 text-sm"><option value="">OH &amp; UG</option><option value="OVERHEAD">Overhead</option><option value="UNDERGROUND">Underground</option></select>
        <div className="relative ml-auto min-w-[16rem] flex-1 sm:flex-none">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search WO, address, UPRN, engineer…" className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm" />
        </div>
      </div>

      <p className="mt-3 text-xs text-gray-400">{data?.date ? `Showing ${jobs.length} of ${data.total} job cards for ${new Date(data.date).toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'short' })}` : ''}</p>

      <div className="mt-1 overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2.5">Region</th><th className="px-3 py-2.5">Slot</th><th className="px-3 py-2.5">WO</th>
              <th className="px-3 py-2.5">Address</th><th className="px-3 py-2.5">Ext</th><th className="px-3 py-2.5">Engineer(s)</th>
              <th className="px-3 py-2.5">Heading</th><th className="px-3 py-2.5">Status</th><th className="px-3 py-2.5">Stock</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && <tr><td colSpan={9} className="px-3 py-6 text-center text-gray-400">Loading…</td></tr>}
            {isError && <tr><td colSpan={9} className="px-3 py-6 text-center text-rose-500">Could not load job cards.</td></tr>}
            {!isLoading && !isError && jobs.length === 0 && <tr><td colSpan={9} className="px-3 py-6 text-center text-gray-400">No job cards for this day / filter.</td></tr>}
            {jobs.map((j) => (
              <tr key={j.id} className="align-top">
                <td className="whitespace-nowrap px-3 py-2 text-gray-600">{j.region}{j.area ? <span className="block text-xs text-gray-400">{j.area}</span> : null}</td>
                <td className="px-3 py-2"><SlotBadge slot={j.time_slot} /></td>
                <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-gray-600">{j.wo_number}</td>
                <td className="px-3 py-2 text-gray-800"><span className="flex items-start gap-1"><MapPinIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-300" />{j.address || '—'}</span>{j.uprn ? <span className="block pl-4 text-xs text-gray-400">UPRN {j.uprn}</span> : null}</td>
                <td className="px-3 py-2"><ExtBadge e={j.externals} /></td>
                <td className="whitespace-nowrap px-3 py-2 text-gray-700">{j.installer1 || <span className="text-rose-500">unassigned</span>}{j.installer2 ? <span className="block text-xs text-gray-500">{j.installer2}</span> : null}</td>
                <td className="whitespace-nowrap px-3 py-2 tabular-nums text-gray-500">{fmtTime(j.heading_to_time)}</td>
                <td className="whitespace-nowrap px-3 py-2 text-gray-600">{j.install_status || '—'}</td>
                <td className="px-3 py-2">{j.stock_status === 'complete' ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700">done</span> : j.stock_status === 'in_progress' ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">part</span> : Number(j.has_stock) === 0 && /complete/i.test(j.install_status || '') ? <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">none</span> : <span className="text-xs text-gray-300">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default JobCards;
